 <template>
  <div class="agreement-modal-overlay" v-if="visible">
    <div class="agreement-modal">
      <div class="agreement-body">
        <div class="agreement-tabs">
          <button 
            class="tab-btn" 
            :class="{ active: activeTab === 'user' }"
            @click="activeTab = 'user'"
          >
            {{ isChinese ? '用户协议' : 'User Agreement' }}
          </button>
          <button 
            class="tab-btn" 
            :class="{ active: activeTab === 'privacy' }"
            @click="activeTab = 'privacy'"
          >
            {{ isChinese ? '隐私条款' : 'Privacy Policy' }}
          </button>
        </div>
        
        <div class="agreement-content-wrapper">
          <div 
            class="agreement-content-text" 
            v-html="currentContent"
          ></div>
        </div>
      </div>
      
      <div class="agreement-footer">
        <button class="btn btn-disagree" @click="handleDisagree">
          {{ isChinese ? '不同意' : 'Disagree' }}
        </button>
        <button class="btn btn-agree" @click="handleAgree">
          {{ isChinese ? '同意' : 'Agree' }}
        </button>
      </div>
    </div>
  </div>
</template>
 
<script>
import { ref, computed } from 'vue'
  
  export default {
    name: 'Agreement',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    agreementData: {
      type: Object,
      default: () => ({
        user_agreement: '',
        privacy_policy: ''
      })
    }
  },
  emits: ['agree', 'disagree'],
    setup(props, { emit }) {
    const activeTab = ref('user')
    
    // 检测浏览器语言
    const isChinese = computed(() => {
      const browserLang = navigator.language || navigator.userLanguage || navigator.browserLanguage
      return browserLang.toLowerCase().startsWith('zh')
    })
    
    const currentContent = computed(() => {
      if (activeTab.value === 'user') {
        return formatAgreementText(props.agreementData.user_agreement || '')
      } else {
        return formatAgreementText(props.agreementData.privacy_policy || '')
      }
    })
    
    // 格式化协议文本（简单处理）
    const formatAgreementText = (text) => {
      if (!text) {
        return isChinese.value ? '协议内容加载中...' : 'Loading agreement content...'
      }
      
      let result = text
      
      // 1. 处理转义的换行符
      result = result.replace(/\\n/g, '\n')
      
      // 2. 处理 Markdown 标题（在换行处理之前）
      result = result.replace(/^##\s+(.+)$/gm, '<h2 class="agreement-h2">$1</h2>')
      result = result.replace(/^###\s+(.+)$/gm, '<h3 class="agreement-h3">$1</h3>')
      
      // 3. 处理加粗文本
      result = result.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      
      // 4. 处理换行符：将连续的换行符（2个或以上）合并为单个换行，避免间距过大
      result = result.replace(/\n{2,}/g, '\n')  // 2个或以上换行符 -> 1个
      result = result.replace(/\n/g, '<br>')  // 单个换行符 -> 1个<br>
      
      // 5. 清理标题前后的多余换行
      result = result.replace(/(<h[23][^>]*>)\s*<br>\s*/g, '$1')
      result = result.replace(/\s*<br>\s*(<\/h[23]>)/g, '$1')
      
      return result
    }
    
    const handleAgree = () => {
      emit('agree', 0) // 0 表示同意
    }
    
    const handleDisagree = () => {
      emit('disagree', 1) // 1 表示不同意
      }
      
      return {
      activeTab,
      currentContent,
      isChinese,
      handleAgree,
      handleDisagree
      }
    }
  }
  </script>
  
<style scoped>
.agreement-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  padding: 20px;
  box-sizing: border-box;
}

.agreement-modal {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 560px;
  max-height: 55vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  overflow: hidden;
}

.agreement-body {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.agreement-tabs {
  display: flex;
  border-bottom: 1px solid #e5e5e5;
  padding: 0;
}

.tab-btn {
  flex: 1;
  padding: 14px 0;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  font-size: 15px;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 400;
}

.tab-btn:hover {
  color: #333;
}

.tab-btn.active {
  color: #007bff;
  border-bottom-color: #007bff;
  font-weight: 500;
}

.agreement-content-wrapper {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background: #fafafa;
}

.agreement-content-text {
  font-size: 14px;
  line-height: 1.7;
  color: #333;
}

.agreement-content-text :deep(.agreement-paragraph) {
  margin: 12px 0;
  line-height: 1.8;
}

.agreement-content-text :deep(.agreement-h2) {
  font-size: 20px;
  font-weight: 600;
  margin: 24px 0 16px 0;
  color: #1a1a1a;
  line-height: 1.4;
}

.agreement-content-text :deep(.agreement-h3) {
  font-size: 16px;
  font-weight: 600;
  margin: 20px 0 12px 0;
  color: #1a1a1a;
  line-height: 1.4;
}

.agreement-content-text :deep(.agreement-list-item) {
  margin: 8px 0;
  padding-left: 8px;
  line-height: 1.8;
}

.agreement-content-text :deep(strong) {
  font-weight: 600;
  color: #1a1a1a;
}

.agreement-footer {
  padding: 16px 20px;
  border-top: 1px solid #e5e5e5;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  background: white;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 80px;
}

.btn-disagree {
  background: #f5f5f5;
  color: #666;
}

.btn-disagree:hover {
  background: #e5e5e5;
}

.btn-agree {
  background: #007bff;
  color: white;
}

.btn-agree:hover {
  background: #0056b3;
}

/* 响应式设计 - 移动端 */
@media (max-width: 768px) {
  .agreement-modal-overlay {
    padding: 16px;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
  }
  
  .agreement-modal {
    max-width: 100%;
    max-height: 70vh;
    border-radius: 12px;
    width: 100%;
    margin: 0;
    box-sizing: border-box;
  }
  
  .agreement-tabs {
    padding: 0;
  }
  
  .tab-btn {
    font-size: 14px;
    padding: 12px 0;
  }
  
  .agreement-content-wrapper {
    padding: 16px;
  }
  
  .agreement-content-text {
    font-size: 13px;
    line-height: 1.6;
  }
  
  .agreement-content-text :deep(.agreement-h2) {
    font-size: 17px;
    margin: 16px 0 10px 0;
  }
  
  .agreement-content-text :deep(.agreement-h3) {
    font-size: 15px;
    margin: 12px 0 8px 0;
  }
  
  .agreement-content-text :deep(.agreement-paragraph) {
    margin: 8px 0;
    line-height: 1.6;
  }
  
  .agreement-content-text :deep(.agreement-list-item) {
    margin: 6px 0;
    padding-left: 6px;
    font-size: 13px;
  }
  
  .agreement-footer {
    padding: 12px 16px;
    flex-direction: row;
    gap: 10px;
  }
  
  .btn {
    flex: 1;
    padding: 10px;
    font-size: 14px;
    min-width: 0;
  }
}

/* 小屏幕设备优化 */
@media (max-width: 480px) {
  .agreement-modal-overlay {
    padding: 12px;
    align-items: center;
    justify-content: center;
  }
  
  .agreement-modal {
    max-height: 75vh;
    border-radius: 12px;
  }
  
  .tab-btn {
    font-size: 13px;
    padding: 10px 0;
  }
  
  .agreement-content-wrapper {
    padding: 12px;
  }
  
  .agreement-content-text {
    font-size: 12px;
    line-height: 1.5;
  }
  
  .agreement-content-text :deep(.agreement-h2) {
    font-size: 16px;
    margin: 14px 0 8px 0;
  }
  
  .agreement-content-text :deep(.agreement-h3) {
    font-size: 14px;
    margin: 10px 0 6px 0;
  }
  
  .agreement-content-text :deep(.agreement-paragraph) {
    margin: 6px 0;
    line-height: 1.5;
  }
  
  .agreement-content-text :deep(.agreement-list-item) {
    margin: 4px 0;
    padding-left: 6px;
    font-size: 12px;
  }
  
  .agreement-footer {
    padding: 10px 12px;
    flex-direction: row;
    gap: 8px;
  }
  
  .btn {
    flex: 1;
    padding: 9px;
    font-size: 13px;
    min-width: 0;
  }
}
</style>
